const RESPONSIVE_VIEWPORT = 'width=device-width, initial-scale=1';

/**
 * Gives the page a device-width layout viewport.
 *
 * `src/index.html` ships no viewport meta, so mobile browsers lay the page out
 * against a ~980px virtual viewport and scale the result down. The MCP consent
 * screens are built on `media.above.sm` and up (768px — see `MCP/MCP.tsx`,
 * `MCP-optional-sites/MCP.tsx`, their footers and `McpInfoCard`), so every one
 * of those queries matches on a real phone and the tablet two-panel layout
 * renders there. Dragging a desktop window narrow works because there the
 * layout viewport *is* the window. `width=device-width` makes the two agree.
 *
 * `maximum-scale` and `user-scalable=no` are deliberately left out — pinch zoom
 * stays available.
 *
 * Called from `ConsentContextProvider` as soon as the token names an MCP flow,
 * which is the earliest point the flow is known: it lands while
 * `/consent/info` is still in flight, so neither that call, the Statsig
 * initialization behind it, nor the first paint of the consent screen happens
 * at the virtual viewport. Only the initial parse and the JWKS fetch and token
 * verification ahead of it do.
 *
 * Scoped to MCP rather than added to `index.html` so the other consent flows
 * keep their current rendering. They are mis-rendering on mobile in the same
 * way and should move too; when they do, put the tag in `index.html` and delete
 * this — an existing tag is left untouched below, so that migration is safe to
 * do in either order.
 */
export const applyResponsiveViewport = (): void => {
  // A tag already in the document means the page has stated its own intent
  // (`index.html` one day, or the Storybook preview iframe today, which is why
  // this bug never showed up in stories). Don't fight it.
  if (document.querySelector('meta[name="viewport"]')) {
    return;
  }

  const meta = document.createElement('meta');
  meta.name = 'viewport';
  meta.content = RESPONSIVE_VIEWPORT;
  document.head.appendChild(meta);
};
